import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { ApiEnvelope } from '../academy/models/permissions.model';
import {
  CrmAccountInfo,
  CrmAccountPayload,
  CrmActivityEntry,
  CrmActivityPayload,
  CrmAmendmentPayload,
  CrmCommunication,
  CrmCollectionReport,
  CrmCommunicationPayload,
  CrmContractPayload,
  CrmCustomer,
  CrmDuplicateMatch,
  CrmCustomerList,
  CrmCustomerLookups,
  CrmCustomerPayload,
  CrmInstallmentDetail,
  CrmPaymentList,
  CrmLead,
  CrmLeadList,
  CrmLeadLookups,
  CrmLeadPayload,
  CrmPage,
  CrmPagination,
  CrmPlatformFeature,
  CrmSalesReport,
  CrmService,
  CrmServicePayload,
  CrmSettings,
  CrmToday,
  CrmStatement,
  CrmSubscriptionPlan,
  CrmSubscriptionTier,
} from '../models/crm.model';

/** Client for the platform CRM API (`/api/v1/super-admin/crm`, PF-0129). */
@Injectable({ providedIn: 'root' })
export class CrmApiService extends BaseService {
  private enabled$?: Observable<boolean>;

  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  private get baseUrl(): string {
    return `${this.rootUrl}/api/v1/super-admin/crm`;
  }

  /** Whether the CRM switch is on; a successful answer is shared for the session, a failure is retried next time. */
  enabled(): Observable<boolean> {
    this.enabled$ ??= this.http.get<ApiEnvelope<{ enabled: boolean }>>(`${this.baseUrl}/status`).pipe(
      map((response) => response.data?.enabled === true),
      shareReplay(1),
    );
    return this.enabled$.pipe(
      catchError(() => {
        this.enabled$ = undefined;
        return of(false);
      }),
    );
  }

  /** Forget the cached switch state (after logout or a new login). */
  resetStatus(): void {
    this.enabled$ = undefined;
  }

  leads(page: number, perPage: number, query: Record<string, string | number | null>): Observable<ApiEnvelope<CrmLeadList>> {
    return this.http.get<ApiEnvelope<CrmLeadList>>(`${this.baseUrl}/leads`, { params: this.params(page, perPage, query) });
  }

  leadLookups(): Observable<ApiEnvelope<CrmLeadLookups>> {
    return this.http.get<ApiEnvelope<CrmLeadLookups>>(`${this.baseUrl}/leads/lookups`);
  }

  leadDuplicates(mobile: string, email: string | null, exceptId?: number): Observable<ApiEnvelope<{ matches: CrmDuplicateMatch[] }>> {
    return this.http.get<ApiEnvelope<{ matches: CrmDuplicateMatch[] }>>(`${this.baseUrl}/leads/duplicates`, {
      params: this.params(1, 1, { mobile, email, except_id: exceptId || null }),
    });
  }

  saveLead(payload: CrmLeadPayload, id?: number): Observable<ApiEnvelope<{ item: CrmLead }>> {
    return id
      ? this.http.put<ApiEnvelope<{ item: CrmLead }>>(`${this.baseUrl}/leads/${id}`, payload)
      : this.http.post<ApiEnvelope<{ item: CrmLead }>>(`${this.baseUrl}/leads`, payload);
  }

  deleteLead(id: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/leads/${id}`);
  }

  /** PF-0162: the wa.me link with the demo message for this lead; the server records the share. */
  shareDemoOnWhatsApp(id: number): Observable<ApiEnvelope<{ url: string; item: CrmLead }>> {
    return this.http.post<ApiEnvelope<{ url: string; item: CrmLead }>>(`${this.baseUrl}/leads/${id}/demo-whatsapp`, {});
  }

  services(page: number, perPage: number, query: Record<string, string | number | null>): Observable<ApiEnvelope<{ items: CrmService[]; pagination: CrmPagination }>> {
    return this.http.get<ApiEnvelope<{ items: CrmService[]; pagination: CrmPagination }>>(`${this.baseUrl}/services`, { params: this.params(page, perPage, query) });
  }

  saveService(payload: CrmServicePayload, id?: number): Observable<ApiEnvelope<{ item: CrmService }>> {
    return id
      ? this.http.put<ApiEnvelope<{ item: CrmService }>>(`${this.baseUrl}/services/${id}`, payload)
      : this.http.post<ApiEnvelope<{ item: CrmService }>>(`${this.baseUrl}/services`, payload);
  }

  /** Features an academy can have switched on, for tying a catalog service to one (PF-0149). */
  platformFeatures(): Observable<ApiEnvelope<{ items: CrmPlatformFeature[] }>> {
    return this.http.get<ApiEnvelope<{ items: CrmPlatformFeature[] }>>(`${this.baseUrl}/platform-features`);
  }

  setServiceActive(id: number, active: boolean): Observable<ApiEnvelope<{ item: CrmService }>> {
    return this.http.patch<ApiEnvelope<{ item: CrmService }>>(`${this.baseUrl}/services/${id}/status`, { is_active: active });
  }

  settings(): Observable<ApiEnvelope<{ settings: CrmSettings }>> {
    return this.http.get<ApiEnvelope<{ settings: CrmSettings }>>(`${this.baseUrl}/settings`);
  }

  saveSettings(settings: CrmSettings): Observable<ApiEnvelope<{ settings: CrmSettings }>> {
    return this.http.put<ApiEnvelope<{ settings: CrmSettings }>>(`${this.baseUrl}/settings`, settings);
  }

  lead(id: number): Observable<ApiEnvelope<{ item: CrmLead }>> {
    return this.http.get<ApiEnvelope<{ item: CrmLead }>>(`${this.baseUrl}/leads/${id}`);
  }

  customers(page: number, perPage: number, query: Record<string, string | number | null>): Observable<ApiEnvelope<CrmCustomerList>> {
    return this.http.get<ApiEnvelope<CrmCustomerList>>(`${this.baseUrl}/customers`, { params: this.params(page, perPage, query) });
  }

  customerLookups(): Observable<ApiEnvelope<CrmCustomerLookups>> {
    return this.http.get<ApiEnvelope<CrmCustomerLookups>>(`${this.baseUrl}/customers/lookups`);
  }

  academies(search: string): Observable<ApiEnvelope<{ items: Array<{ id: number; title: string }> }>> {
    return this.http.get<ApiEnvelope<{ items: Array<{ id: number; title: string }> }>>(`${this.baseUrl}/customers/academies`, { params: { search } });
  }

  customer(id: number): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.get<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}`);
  }

  /** Creates (converting `lead_id` when given) or updates a customer and its representative. */
  saveCustomer(payload: CrmCustomerPayload, id?: number): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return id
      ? this.http.put<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}`, payload)
      : this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers`, payload);
  }

  saveContract(id: number, payload: CrmContractPayload): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.put<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}/contract`, payload);
  }

  /** submit | return-to-draft | discount/approve | discount/reject */
  customerAction(id: number, action: 'submit' | 'return-to-draft' | 'discount/approve' | 'discount/reject', note?: string): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}/${action}`, note ? { note } : {});
  }

  /** Turns on the service's platform feature for the customer's linked academy (PF-0149). */
  activateService(id: number, serviceId: number): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}/services/${serviceId}/activate`, {});
  }

  /** Turns the service's platform feature off for the customer's academy (PF-0159). */
  deactivateService(id: number, serviceId: number): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}/services/${serviceId}/deactivate`, {});
  }

  /** Plan tiers of the plans system, for the account wizard and the catalog (PF-0159). */
  subscriptionPlans(): Observable<ApiEnvelope<{ items: CrmSubscriptionPlan[] }>> {
    return this.http.get<ApiEnvelope<{ items: CrmSubscriptionPlan[] }>>(`${this.baseUrl}/subscription-plans`);
  }

  /** Whether an account can be created for the customer, with the wizard's prefill (PF-0159). */
  accountPrefill(id: number): Observable<ApiEnvelope<CrmAccountInfo>> {
    return this.http.get<ApiEnvelope<CrmAccountInfo>>(`${this.baseUrl}/customers/${id}/account`);
  }

  /** Creates the academy, its manager and the subscription on the contract period (PF-0159). */
  createAccount(id: number, payload: CrmAccountPayload): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}/account`, payload);
  }

  /** Links an existing main academy and moves its subscription to the tier and contract period (PF-0159). */
  linkAccount(id: number, academyId: number, tier: CrmSubscriptionTier): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}/account/link`, { academy_id: academyId, tier });
  }

  /** Draft amendment: services bought mid-term, ending with the current contract (PF-0156). */
  saveAmendment(customerId: number, payload: CrmAmendmentPayload, amendmentId?: number): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    const url = `${this.baseUrl}/customers/${customerId}/amendments`;
    return amendmentId
      ? this.http.put<ApiEnvelope<{ item: CrmCustomer }>>(`${url}/${amendmentId}`, payload)
      : this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(url, payload);
  }

  deleteAmendment(customerId: number, amendmentId: number): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.delete<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${customerId}/amendments/${amendmentId}`);
  }

  /** submit | return-to-draft | discount/approve | discount/reject */
  amendmentAction(
    customerId: number,
    amendmentId: number,
    action: 'submit' | 'return-to-draft' | 'discount/approve' | 'discount/reject',
    note?: string,
  ): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${customerId}/amendments/${amendmentId}/${action}`, note ? { note } : {});
  }

  /** Uploads the signed addendum; the amendment becomes active when it was awaiting signature. */
  uploadAmendmentDocument(customerId: number, amendmentId: number, title: string | null, file: File): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    const body = new FormData();
    if (title) {
      body.append('title', title);
    }
    body.append('file', file);
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${customerId}/amendments/${amendmentId}/documents`, body);
  }

  /** Expired customer only: keeps the old contract as history and opens a new draft contract. */
  renew(customerId: number): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${customerId}/renew`, {});
  }

  /** Account statement of one customer (PF-0154): services, totals, installments and payments received. */
  statement(id: number): Observable<ApiEnvelope<{ statement: CrmStatement }>> {
    return this.http.get<ApiEnvelope<{ statement: CrmStatement }>>(`${this.baseUrl}/customers/${id}/statement`);
  }

  uploadDocument(id: number, type: string, title: string | null, file: File): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    const body = new FormData();
    body.append('type', type);
    if (title) {
      body.append('title', title);
    }
    body.append('file', file);
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}/documents`, body);
  }

  /** Generates the technical or financial offer PDF and stores it in the customer's documents (PF-0165). */
  generateOffer(id: number, kind: 'technical' | 'financial'): Observable<ApiEnvelope<{ item: CrmCustomer }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCustomer }>>(`${this.baseUrl}/customers/${id}/offers/${kind}`, {});
  }

  downloadDocument(id: number, documentId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/customers/${id}/documents/${documentId}`, { responseType: 'blob' });
  }

  deleteDocument(id: number, documentId: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/customers/${id}/documents/${documentId}`);
  }

  payments(page: number, perPage: number, query: Record<string, string | number | null>): Observable<ApiEnvelope<CrmPaymentList>> {
    return this.http.get<ApiEnvelope<CrmPaymentList>>(`${this.baseUrl}/payments`, { params: this.params(page, perPage, query) });
  }

  installment(id: number): Observable<ApiEnvelope<{ item: CrmInstallmentDetail }>> {
    return this.http.get<ApiEnvelope<{ item: CrmInstallmentDetail }>>(`${this.baseUrl}/payments/${id}`);
  }

  /** Records a received transfer; partial amounts are allowed up to what is left. */
  collect(id: number, receivedAt: string, amount: number, notes: string | null, file: File | null): Observable<ApiEnvelope<{ item: CrmInstallmentDetail }>> {
    const body = new FormData();
    body.append('received_at', receivedAt);
    body.append('amount', String(amount));
    if (notes) {
      body.append('notes', notes);
    }
    if (file) {
      body.append('file', file);
    }
    return this.http.post<ApiEnvelope<{ item: CrmInstallmentDetail }>>(`${this.baseUrl}/payments/${id}/collect`, body);
  }

  attachToInstallment(id: number, file: File): Observable<ApiEnvelope<{ item: CrmInstallmentDetail }>> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<ApiEnvelope<{ item: CrmInstallmentDetail }>>(`${this.baseUrl}/payments/${id}/attachments`, body);
  }

  downloadInstallmentAttachment(id: number, attachmentId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/payments/${id}/attachments/${attachmentId}`, { responseType: 'blob' });
  }

  /** The server checks the 3-day window, logs the reminder and returns the wa.me link. */
  reminder(id: number): Observable<ApiEnvelope<{ url: string; message: string }>> {
    return this.http.post<ApiEnvelope<{ url: string; message: string }>>(`${this.baseUrl}/payments/${id}/reminder`, {});
  }

  communications(customerId: number, page: number, perPage: number): Observable<ApiEnvelope<CrmPage<CrmCommunication>>> {
    return this.http.get<ApiEnvelope<CrmPage<CrmCommunication>>>(`${this.baseUrl}/customers/${customerId}/communications`, { params: this.params(page, perPage, {}) });
  }

  addCommunication(customerId: number, payload: CrmCommunicationPayload): Observable<ApiEnvelope<{ item: CrmCommunication }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCommunication }>>(`${this.baseUrl}/customers/${customerId}/communications`, payload);
  }

  completeReminder(customerId: number, communicationId: number): Observable<ApiEnvelope<{ item: CrmCommunication }>> {
    return this.http.post<ApiEnvelope<{ item: CrmCommunication }>>(`${this.baseUrl}/customers/${customerId}/communications/${communicationId}/reminder-done`, {});
  }

  /** Relationship log, newest first; `kind` is system | manual. */
  activities(customerId: number, page: number, perPage: number, kind: 'system' | 'manual' | null): Observable<ApiEnvelope<CrmPage<CrmActivityEntry>>> {
    return this.http.get<ApiEnvelope<CrmPage<CrmActivityEntry>>>(`${this.baseUrl}/customers/${customerId}/activities`, { params: this.params(page, perPage, { kind }) });
  }

  /** Records a staff milestone and answers with the first page of the log. */
  addActivity(customerId: number, payload: CrmActivityPayload): Observable<ApiEnvelope<CrmPage<CrmActivityEntry>>> {
    return this.http.post<ApiEnvelope<CrmPage<CrmActivityEntry>>>(`${this.baseUrl}/customers/${customerId}/activities`, payload);
  }

  /** The CRM home: the sections this user may see, and the total of their alerts. */
  today(): Observable<ApiEnvelope<CrmToday>> {
    return this.http.get<ApiEnvelope<CrmToday>>(`${this.baseUrl}/today`);
  }

  salesReport(from: string, to: string): Observable<ApiEnvelope<{ report: CrmSalesReport }>> {
    return this.http.get<ApiEnvelope<{ report: CrmSalesReport }>>(`${this.baseUrl}/reports/sales`, { params: { from, to } });
  }

  collectionReport(from: string, to: string): Observable<ApiEnvelope<{ report: CrmCollectionReport }>> {
    return this.http.get<ApiEnvelope<{ report: CrmCollectionReport }>>(`${this.baseUrl}/reports/collection`, { params: { from, to } });
  }

  private params(page: number, perPage: number, query: Record<string, string | number | null>): HttpParams {
    let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    for (const [key, value] of Object.entries(query)) {
      if (value !== null && value !== '' && value !== undefined) {
        params = params.set(key, String(value));
      }
    }
    return params;
  }
}
